import React, { useState, useEffect } from 'react';
import { FragebogenTakerPage } from './FragebogenTakerPage';
import { ErrorPage } from './ErrorPage';
import pb from '../../pocketbaseClient';
import { collectBrowserMetadata } from '../../utils/browserMetadata.js';
import { normalizeQuestionnaireData } from '../../utils/questionnairePages';

// Function to validate campaign data structure
const validateCampaignData = (campaign) => {
  if (!campaign) return false;
  
  const requiredFields = ['id', 'name', 'status'];
  const hasRequiredFields = requiredFields.every(field => campaign[field] !== undefined && campaign[field] !== null);
  
  if (!hasRequiredFields) {
    console.error('Campaign missing required fields:', {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      hasAllRequired: hasRequiredFields
    });
    return false;
  }
  
  if (campaign.status !== 'online') {
    console.error('Campaign is not online:', campaign.status);
    return false;
  }
  
  return true;
};

export function PublicFragebogenPage({ campaignId }) {
    const [fragebogenData, setFragebogenData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadFragebogen = async () => {
            try {
                // Try to load campaign data from PocketBase (public access)
                const campaign = await pb.collection('campaigns').getOne(campaignId);
                
                // Debug: Log campaign data to help identify issues
                console.log('Campaign data loaded:', {
                    id: campaign?.id,
                    name: campaign?.name,
                    status: campaign?.status,
                    hasQuestionnaireData: !!campaign?.questionnaire_data,
                    questionnaireDataType: typeof campaign?.questionnaire_data,
                    questionnaireDataLength: campaign?.questionnaire_data?.length || 0
                });
                
                // Validate campaign data structure
                if (!validateCampaignData(campaign)) {
                    console.error('Campaign validation failed for ID:', campaignId);
                    setError('Kampagne nicht verfügbar');
                    return;
                }

                if (campaign.questionnaire_data) {
                    try {
                        // Parse questionnaire_data if it's a string
                        const questionnaireData = typeof campaign.questionnaire_data === 'string' 
                            ? JSON.parse(campaign.questionnaire_data) 
                            : campaign.questionnaire_data;
                        
                        // Debug: Log questionnaire data structure
                        console.log('Questionnaire data parsed:', {
                            hasTitle: !!questionnaireData?.title,
                            hasQuestions: !!questionnaireData?.questions,
                            questionsCount: questionnaireData?.questions?.length || 0,
                            hasDescription: !!questionnaireData?.description
                        });
                        
                        setFragebogenData(normalizeQuestionnaireData(questionnaireData));
                    } catch (parseError) {
                        console.error('Error parsing questionnaire data:', parseError);
                        setError('Fragebogen-Daten sind beschädigt');
                    }
                } else {
                    console.error('No questionnaire data found for campaign:', campaignId);
                    setError('Fragebogen nicht verfügbar');
                }
            } catch (err) {
                console.error('Error loading fragebogen from PocketBase:', err);
                
                // If PocketBase access fails, try to load from localStorage as fallback
                try {
                    const fragebogenService = (await import('../../services/FragebogenService')).default;
                    const result = fragebogenService.getFragebogen(campaignId);
                    if (result.success && result.data) {
                        setFragebogenData(normalizeQuestionnaireData(result.data));
                    } else {
                        setError('Fragebogen nicht gefunden');
                    }
                } catch (localErr) {
                    console.error('Error loading fragebogen from localStorage:', localErr);
                    setError('Fragebogen nicht verfügbar');
                }
            } finally {
                setLoading(false);
            }
        };

        if (campaignId) {
            loadFragebogen();
        } else {
            setError('Keine Kampagnen-ID angegeben');
            setLoading(false);
        }
    }, [campaignId]);

    const handleFragebogenSubmission = async (submission) => {
        try {
            // Collect browser metadata
            const browserMetadata = collectBrowserMetadata();
            
            // Create applicant data for PocketBase storage (score added after evaluation)
            const applicantData = {
                leadID: `lead_${Date.now()}`,
                name: submission.contactInfo.name,
                email: submission.contactInfo.email,
                phone: submission.contactInfo.phone || '',
                status: 'Unbearbeitet',
                createdAt: new Date().toISOString(),
                lastChange: new Date().toISOString(),
                notes: '',
                experience: '',
                skills: '',
                rawFunnelData: [],
                meta_data: submission.meta_data || browserMetadata,
                answerLog: submission.answerLog,
                fragebogenResponses: Object.entries(submission.answers).map(([questionId, answer]) => {
                    const question = fragebogenData?.questions?.find(q => String(q.id) === String(questionId));
                    return {
                        questionId,
                        questionText: question?.text || '',
                        questionType: question?.type || '',
                        answer: answer,
                        answeredAt: new Date().toISOString()
                    };
                })
            };
            // Save applicant to campaign first
            try {
                const campaign = await pb.collection('campaigns').getOne(campaignId);
                const updatedApplicants = [...(campaign.applicants || []), applicantData];
                
                await pb.collection('campaigns').update(campaignId, {
                    applicants: updatedApplicants,
                    applicantCount: (campaign.applicantCount || 0) + 1
                });
                
                console.log('Fragebogen submission saved to PocketBase:', applicantData);
            } catch (pbError) {
                console.error('Error saving to PocketBase, saving locally:', pbError);
                const storageKey = `public_fragebogen_submission_${campaignId}_${Date.now()}`;
                localStorage.setItem(storageKey, JSON.stringify(applicantData));
                const submissionsKey = `public_fragebogen_submissions_${campaignId}`;
                const existingSubmissions = JSON.parse(localStorage.getItem(submissionsKey) || '[]');
                existingSubmissions.push(applicantData);
                localStorage.setItem(submissionsKey, JSON.stringify(existingSubmissions));
            }
            return null;
        } catch (error) {
            console.error('Error submitting fragebogen:', error);
            alert('Fehler beim Einreichen der Bewerbung');
            throw error;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 dark:text-slate-400">Quiz wird geladen...</p>
                </div>
            </div>
        );
    }

    if (error || !fragebogenData) {
        // Show 404 page for offline campaigns or not found campaigns
        if (error === 'Kampagne ist offline' || error === 'Kampagne nicht gefunden') {
            return (
                <ErrorPage
                    title="Fragebogen nicht verfügbar"
                    message={error === 'Kampagne ist offline' ? 'Diese Kampagne ist derzeit offline und der Fragebogen nicht verfügbar.' : 'Diese Kampagne wurde nicht gefunden.'}
                    subtitle="Mögliche Gründe: Die Kampagne wurde beendet, ist offline oder der Link ist nicht mehr gültig."
                    errorCode="404"
                    errorType="Not Found"
                    showBackButton={true}
                />
            );
        }
        
        // Show generic error for other issues
        return (
            <ErrorPage
                title="Fragebogen nicht verfügbar"
                message={error || 'Der angeforderte Fragebogen konnte nicht gefunden werden.'}
                subtitle="Bitte versuchen Sie es später erneut oder wenden Sie sich an den Administrator."
                errorCode="500"
                errorType="Internal Server Error"
                showBackButton={true}
            />
        );
    }

    return (
        <FragebogenTakerPage
            fragebogenData={fragebogenData}
            campaignId={campaignId}
            onBack={() => window.history.back()}
            onSubmit={handleFragebogenSubmission}
        />
    );
}
